import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Stack,
  Button,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Card,
  CardContent,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Upload,
  Description,
  PictureAsPdf,
  TextSnippet,
  Search,
  CloudUpload,
  Assignment,
  Science,
  CheckCircle,
  Warning,
  ArrowBack,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import axiosClient from '../api/axiosClient';
import { FileProcessor, type ProcessedFile } from '../services/fileProcessor';

const ManageFilesProcessPage: React.FC = () => {
  const { t } = useTranslation();
  const [processing, setProcessing] = React.useState<boolean>(false);
  const [result, setResult] = React.useState<{ success: boolean; operationId?: number; message?: string } | null>(null);
  const [error, setError] = React.useState<string>('');
  const [selectedFilesFromExplorer, setSelectedFilesFromExplorer] = React.useState<ProcessedFile[]>([]);
  const [originalFilesFromExplorer, setOriginalFilesFromExplorer] = React.useState<File[]>([]);
  const [nifDetectionMode, setNifDetectionMode] = React.useState<'filename' | 'content'>('filename');
  const [testResults, setTestResults] = React.useState<any>(null);
  const [testing, setTesting] = React.useState<boolean>(false);
  const [testTabValue, setTestTabValue] = React.useState<number>(0);
  const [showTestResults, setShowTestResults] = React.useState<boolean>(false);

  const handleProcessDirectory = async () => {
    if (selectedFilesFromExplorer.length === 0) {
      setError('No hay archivos procesados para enviar');
      return;
    }

    setProcessing(true);
    setError('');
    setResult(null);

    try {
      // Crear FormData para enviar archivos originales
      const formData = new FormData();
      
      // Agregar metadatos de archivos procesados
      const processedFilesData = selectedFilesFromExplorer.map(file => ({
        name: file.name,
        path: file.path,
        size: file.size,
        type: file.type,
        content: file.content.substring(0, 1000), // Solo primeros 1000 caracteres para debugging
        extractedNif: file.extractedNif,
        success: file.success,
        error: file.error,
      }));

      formData.append('directoryPath', '/uploaded-via-dnd');
      formData.append('processedFiles', JSON.stringify(processedFilesData));
      formData.append('nifDetectionMode', nifDetectionMode);

      // Agregar archivos originales
      originalFilesFromExplorer.forEach((file, index) => {
        formData.append(`originalFile_${index}`, file);
      });


      const response = await axiosClient.post('/manage-files/process-directory', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000,
      });
      
      setResult({
        success: Boolean(response?.data?.success),
        operationId: response?.data?.operationId,
        message: response?.data?.message,
      });
    } catch (err: any) {
      setError(err.response?.data?.message || t('manageFiles.process.errors.generic'));
    } finally {
      setProcessing(false);
    }
  };

  const handleTestDirectory = async () => {
    if (selectedFilesFromExplorer.length === 0) {
      setError('No hay archivos procesados para testear');
      return;
    }

    setTesting(true);
    setError('');
    setTestResults(null);

    try {
      // Crear FormData para enviar archivos originales
      const formData = new FormData();
      
      // Agregar metadatos de archivos procesados
      const processedFilesData = selectedFilesFromExplorer.map(file => ({
        name: file.name,
        path: file.path,
        size: file.size,
        type: file.type,
        content: file.content.substring(0, 1000), // Solo primeros 1000 caracteres para debugging
        extractedNif: file.extractedNif,
        success: file.success,
        error: file.error,
      }));

      formData.append('directoryPath', '/uploaded-via-dnd');
      formData.append('processedFiles', JSON.stringify(processedFilesData));
      formData.append('nifDetectionMode', nifDetectionMode);

      // Agregar archivos originales
      originalFilesFromExplorer.forEach((file, index) => {
        formData.append(`originalFile_${index}`, file);
      });


      const response = await axiosClient.post('/manage-files/test-directory', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 600000,
      });
      
      setTestResults(response.data.testResults);
      setShowTestResults(true); // Mostrar la capa de resultados
    } catch (err: any) {
      setError(err.response?.data?.message || t('manageFiles.process.errors.generic'));
    } finally {
      setTesting(false);
    }
  };

  const handleExportTestResults = async () => {
    if (!testResults) return;

    try {
      // Importar XLSX dinámicamente
      const XLSX = await import('xlsx');
      const workbook = XLSX.utils.book_new();

    // Hoja 1: Archivos correctos
    const correctData = testResults.correctResults.map((file: any) => ({
      'Nombre del Archivo': file.fileName,
      'NIF Extraído': file.extractedNif,
      'Tipo de Archivo': file.fileType,
      'Tamaño (bytes)': file.fileSize,
    }));

    const correctSheet = XLSX.utils.json_to_sheet(correctData);
    XLSX.utils.book_append_sheet(workbook, correctSheet, 'Archivos Correctos');

    // Hoja 2: Archivos con errores
    const errorData = testResults.errorResults.map((file: any) => ({
      'Nombre del Archivo': file.fileName,
      'NIF Extraído': file.extractedNif || 'N/A',
      'Tipo de Error': file.errorType,
      'Mensaje de Error': file.errorMessage,
      'Tipo de Archivo': file.fileType,
      'Tamaño (bytes)': file.fileSize,
    }));

    const errorSheet = XLSX.utils.json_to_sheet(errorData);
    XLSX.utils.book_append_sheet(workbook, errorSheet, 'Archivos con Errores');

    // Hoja 3: Archivos no procesados
    const unprocessedData = testResults.unprocessedResults.map((file: any) => ({
      'Nombre del Archivo': file.fileName,
      'Tipo de Archivo': file.fileType,
      'Motivo': file.reason,
      'Tamaño (bytes)': file.fileSize,
    }));

    const unprocessedSheet = XLSX.utils.json_to_sheet(unprocessedData);
    XLSX.utils.book_append_sheet(workbook, unprocessedSheet, 'Archivos No Procesados');

    // Generar nombre de archivo con timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
    const fileName = `test-results-${timestamp}.xlsx`;

      // Descargar archivo
      XLSX.writeFile(workbook, fileName);
    } catch (error) {
      console.error('Error al exportar resultados:', error);
      setError('Error al exportar los resultados a Excel');
    }
  };

  // handleSelectDirectory obsoleto: eliminado

  const handleBackToDirectory = () => {
    setShowTestResults(false);
  };

  // Drag & Drop handlers
  const onDropFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    setError('');
    setTestResults(null);
    setShowTestResults(false);
    const arr = Array.from(files);
    try {
      const processed = await FileProcessor.processFiles(arr, nifDetectionMode);
      setSelectedFilesFromExplorer(processed.processedFiles);
      setOriginalFilesFromExplorer(arr);
    } catch (e: any) {
      setError(e.message || 'Error procesando archivos');
    }
  };

  // Reprocesar archivos cuando cambia el modo de detección
  React.useEffect(() => {
    if (originalFilesFromExplorer.length > 0) {
      const reprocessFiles = async () => {
        try {
          const processed = await FileProcessor.processFiles(originalFilesFromExplorer, nifDetectionMode);
          setSelectedFilesFromExplorer(processed.processedFiles);
        } catch (e: any) {
          setError(e.message || 'Error reprocesando archivos');
        }
      };
      reprocessFiles();
    }
  }, [nifDetectionMode]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    onDropFiles(e.dataTransfer.files);
  };

  const fileInputRef = React.useRef<HTMLInputElement | null>(null);

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
        {t('manageFiles.process.title')}
      </Typography>

      <Box sx={{ display: 'flex', gap: 3 }}>
        {/* Main Content */}
        <Box sx={{ flex: 2 }}>
          {!showTestResults ? (
            /* CAPA 1: Drag & Drop de archivos */
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('manageFiles.process.selectFiles', 'Selecciona o arrastra archivos')}
              </Typography>
            
            <Stack spacing={2}>
              {/* Dropzone */}
              <Box
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                sx={{
                  border: '2px dashed',
                  borderColor: 'divider',
                  borderRadius: 2,
                  p: 4,
                  textAlign: 'center',
                  bgcolor: 'background.default',
                  cursor: 'pointer',
                }}
                onClick={() => fileInputRef.current?.click()}
              >
                <CloudUpload sx={{ fontSize: 42, mb: 1 }} color="primary" />
                <Typography variant="body1">
                  {t('manageFiles.process.dropHint', 'Arrastra aquí tus archivos o haz clic para seleccionarlos')}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {t('manageFiles.process.supportedShort', 'PDF, DOCX/DOC/ODT, TXT')}
                </Typography>
                <input
                  ref={fileInputRef}
                  type="file"
                  multiple
                  hidden
                  onChange={(e) => onDropFiles(e.target.files)}
                />
              </Box>

              {/* Lista de archivos seleccionados */}
              {selectedFilesFromExplorer.length > 0 && (
                <Paper sx={{ p: 2, mt: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    {t('manageFiles.process.selectedFiles', 'Archivos seleccionados')} ({selectedFilesFromExplorer.length})
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('manageFiles.process.fileName', 'Nombre del archivo')}</TableCell>
                          <TableCell align="center">{t('manageFiles.process.extractedNif', 'NIF extraído')}</TableCell>
                          <TableCell align="right">{t('manageFiles.process.fileSize', 'Tamaño')}</TableCell>
                          <TableCell align="center">{t('manageFiles.process.status', 'Estado')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedFilesFromExplorer.map((file, index) => {
                          const getFileIcon = () => {
                            const ext = file.name.toLowerCase().split('.').pop();
                            if (ext === 'pdf') return <PictureAsPdf color="error" />;
                            if (['docx', 'doc', 'odt'].includes(ext || '')) return <Description color="primary" />;
                            if (ext === 'txt') return <TextSnippet color="action" />;
                            return <Description />;
                          };

                          const formatFileSize = (bytes: number): string => {
                            if (bytes === 0) return '0 B';
                            const k = 1024;
                            const sizes = ['B', 'KB', 'MB', 'GB'];
                            const i = Math.floor(Math.log(bytes) / Math.log(k));
                            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
                          };

                          return (
                            <TableRow key={index}>
                              <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  {getFileIcon()}
                                  <Typography variant="body2" noWrap sx={{ maxWidth: 400 }}>
                                    {file.name}
                                  </Typography>
                                </Box>
                              </TableCell>
                              <TableCell align="center">
                                {file.extractedNif ? (
                                  <Chip label={file.extractedNif} color="success" size="small" />
                                ) : (
                                  <Typography variant="body2" color="textSecondary">-</Typography>
                                )}
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="body2">
                                  {formatFileSize(file.size)}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {file.success ? (
                                  <Chip icon={<CheckCircle />} label={t('manageFiles.process.success', 'Exitoso')} color="success" size="small" />
                                ) : (
                                  <Chip icon={<Warning />} label={t('manageFiles.process.error', 'Error')} color="error" size="small" />
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {selectedFilesFromExplorer.some(f => !f.success) && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        {t('manageFiles.process.someFilesFailed', 'Algunos archivos no se procesaron correctamente. Revisa los errores antes de continuar.')}
                      </Typography>
                    </Alert>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setSelectedFilesFromExplorer([]);
                      setOriginalFilesFromExplorer([]);
                    }}
                    sx={{ mt: 2 }}
                  >
                    {t('manageFiles.process.clearFiles', 'Limpiar archivos')}
                  </Button>
                </Paper>
              )}

              {/* Modo de detección */}
              <Stack direction="row" spacing={2}>
                <Button
                  variant={nifDetectionMode === 'filename' ? 'contained' : 'outlined'}
                  onClick={() => setNifDetectionMode('filename')}
                  size="small"
                >
                  {t('manageFiles.process.modeFilename', 'Detectar NIF en nombre')}
                </Button>
                <Button
                  variant={nifDetectionMode === 'content' ? 'contained' : 'outlined'}
                  onClick={() => setNifDetectionMode('content')}
                  size="small"
                >
                  {t('manageFiles.process.modeContent', 'Detectar NIF en contenido')}
                </Button>
              </Stack>

              <Stack direction="row" spacing={2} sx={{ alignSelf: 'flex-start' }}>
                <Button
                  variant="outlined"
                  size="large"
                  startIcon={<Science />}
                  onClick={handleTestDirectory}
                  disabled={testing || processing || selectedFilesFromExplorer.length === 0}
                >
                  {testing ? t('manageFiles.process.testing') : t('manageFiles.process.testProcessing')}
                </Button>
                
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Upload />}
                  onClick={handleProcessDirectory}
                  disabled={processing || testing || selectedFilesFromExplorer.length === 0}
                >
                  {processing ? t('manageFiles.process.processing') : t('manageFiles.process.startProcessing')}
                </Button>
              </Stack>

              {processing && (
                <Box>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('manageFiles.process.processingMessage')}
                  </Typography>
                </Box>
              )}

              {testing && (
                <Box>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {t('manageFiles.process.testProcessingDescription')}
                  </Typography>
                </Box>
              )}

              {error && (
                <Alert severity="error">
                  {error}
                </Alert>
              )}

              {result && (
                <Alert severity={result.success ? 'success' : 'info'}>
                  <Typography variant="h6" gutterBottom>
                    {t('manageFiles.process.result')}
                  </Typography>
                  <Typography variant="body2" gutterBottom>
                    {result.message || t('manageFiles.process.successMessage')}
                  </Typography>
                  {result.operationId && (
                  <Typography variant="body2" gutterBottom>
                      {t('manageFiles.process.operationId')}: {result.operationId}
                  </Typography>
                  )}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => window.location.href = '/manage-files/dashboard'}
                    sx={{ mt: 1 }}
                  >
                    {t('manageFiles.process.viewDashboard')}
                  </Button>
                </Alert>
              )}
            </Stack>
          </Paper>
          ) : (
            /* CAPA 2: Resultados del test */
            <Paper sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  {t('manageFiles.process.testResults')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBack />}
                  onClick={handleBackToDirectory}
                >
                  Volver
                </Button>
              </Box>

              <Typography variant="body1" color="textSecondary" sx={{ mb: 3 }}>
                {t('manageFiles.process.testResultsDescription')}
              </Typography>

              {/* Summary Cards */}
              <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {t('manageFiles.process.totalFilesInDirectory')}
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {testResults?.totalFilesInDirectory || 0}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {t('manageFiles.process.totalFilesProcessed')}
                    </Typography>
                    <Typography variant="h4" color="info.main">
                      {testResults?.totalFilesProcessed || 0}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {t('manageFiles.process.correctFiles')}
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {testResults?.correctFiles || 0}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {t('manageFiles.process.errorFiles')}
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {testResults?.errorFiles || 0}
                    </Typography>
                  </CardContent>
                </Card>
                <Card sx={{ flex: 1 }}>
                  <CardContent>
                    <Typography variant="h6" color="textSecondary" gutterBottom>
                      {t('manageFiles.process.unprocessedFiles')}
                    </Typography>
                    <Typography variant="h4" color="warning.main">
                      {testResults?.unprocessedFiles || 0}
                    </Typography>
                  </CardContent>
                </Card>
              </Stack>

              {/* Export Button */}
              <Box sx={{ mb: 3 }}>
                <Button
                  variant="outlined"
                  startIcon={<Upload />}
                  onClick={handleExportTestResults}
                >
                  {t('manageFiles.process.exportResults')}
                </Button>
              </Box>

              {/* Tabs for Results */}
              <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={testTabValue} onChange={(_, newValue) => setTestTabValue(newValue)}>
                  <Tab 
                    label={`${t('manageFiles.process.correctFiles')} (${testResults?.correctFiles || 0})`} 
                    icon={<CheckCircle />}
                  />
                  <Tab 
                    label={`${t('manageFiles.process.errorFiles')} (${testResults?.errorFiles || 0})`} 
                    icon={<Warning />}
                  />
                  <Tab 
                    label={`${t('manageFiles.process.unprocessedFiles')} (${testResults?.unprocessedFiles || 0})`} 
                    icon={<Assignment />}
                  />
                </Tabs>
              </Box>

              {/* Correct Files Tab */}
              {testTabValue === 0 && (
                <Box sx={{ mt: 2 }}>
                  {!testResults?.correctResults || testResults.correctResults.length === 0 ? (
                    <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                      {t('manageFiles.process.noTestResults')}
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('manageFiles.process.fileName')}</TableCell>
                            <TableCell>{t('manageFiles.process.extractedNif')}</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell align="right">Tamaño</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {testResults.correctResults.map((file: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{file.fileName}</TableCell>
                              <TableCell>
                                <Chip label={file.extractedNif} color="success" size="small" />
                              </TableCell>
                              <TableCell>{file.fileType}</TableCell>
                              <TableCell align="right">{(file.fileSize / 1024).toFixed(1)} KB</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Error Files Tab */}
              {testTabValue === 1 && (
                <Box sx={{ mt: 2 }}>
                  {!testResults?.errorResults || testResults.errorResults.length === 0 ? (
                    <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                      {t('manageFiles.process.noTestResults')}
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('manageFiles.process.fileName')}</TableCell>
                            <TableCell>{t('manageFiles.process.extractedNif')}</TableCell>
                            <TableCell>{t('manageFiles.process.errorType')}</TableCell>
                            <TableCell>{t('manageFiles.process.errorMessage')}</TableCell>
                            <TableCell align="right">Tamaño</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {testResults.errorResults.map((file: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{file.fileName}</TableCell>
                              <TableCell>
                                {file.extractedNif ? (
                                  <Chip label={file.extractedNif} color="warning" size="small" />
                                ) : (
                                  <Typography variant="body2" color="textSecondary">N/A</Typography>
                                )}
                              </TableCell>
                              <TableCell>
                                <Chip 
                                  label={file.errorType} 
                                  color="error" 
                                  size="small" 
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell>{file.errorMessage}</TableCell>
                              <TableCell align="right">{(file.fileSize / 1024).toFixed(1)} KB</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Unprocessed Files Tab */}
              {testTabValue === 2 && (
                <Box sx={{ mt: 2 }}>
                  {!testResults?.unprocessedResults || testResults.unprocessedResults.length === 0 ? (
                    <Typography variant="body1" color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
                      {t('manageFiles.process.noTestResults')}
                    </Typography>
                  ) : (
                    <TableContainer>
                      <Table>
                        <TableHead>
                          <TableRow>
                            <TableCell>{t('manageFiles.process.fileName')}</TableCell>
                            <TableCell>Tipo</TableCell>
                            <TableCell>{t('manageFiles.process.reason')}</TableCell>
                            <TableCell align="right">Tamaño</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {testResults.unprocessedResults.map((file: any, index: number) => (
                            <TableRow key={index}>
                              <TableCell>{file.fileName}</TableCell>
                              <TableCell>{file.fileType}</TableCell>
                              <TableCell>
                                <Chip 
                                  label={file.reason} 
                                  color="warning" 
                                  size="small" 
                                  variant="outlined"
                                />
                              </TableCell>
                              <TableCell align="right">{(file.fileSize / 1024).toFixed(1)} KB</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  )}
                </Box>
              )}

              {/* Process Button */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Upload />}
                  onClick={handleProcessDirectory}
                  disabled={processing || selectedFilesFromExplorer.length === 0}
                >
                  {processing ? t('manageFiles.process.processing') : t('manageFiles.process.startProcessing')}
                </Button>
              </Box>
            </Paper>
          )}
        </Box>

        {/* Sidebar */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              {t('manageFiles.process.supportedFormats')}
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <PictureAsPdf color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="PDF"
                  secondary="Portable Document Format"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Description color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="DOCX/DOC/ODT"
                  secondary="Microsoft Word & OpenDocument Text"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <TextSnippet color="action" />
                </ListItemIcon>
                <ListItemText 
                  primary="TXT"
                  secondary="Plain Text Files"
                />
              </ListItem>
            </List>

            <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
              {t('manageFiles.process.processInfo')}
            </Typography>
            
            <List>
              <ListItem>
                <ListItemIcon>
                  <Search color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={t('manageFiles.process.step1')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Assignment color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={t('manageFiles.process.step2')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <Search color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={t('manageFiles.process.step3')}
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CloudUpload color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary={t('manageFiles.process.step4')}
                />
              </ListItem>
            </List>

            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('manageFiles.process.warnings')}
              </Typography>
              <Typography variant="body2">
                {t('manageFiles.process.warning1')}
              </Typography>
              <Typography variant="body2">
                {t('manageFiles.process.warning2')}
              </Typography>
            </Alert>
          </Paper>
        </Box>
      </Box>


      {/* Explorador de directorios (obsoleto) */}
    </Box>
  );
};

export default ManageFilesProcessPage;